import React, { use, useCallback, useRef } from "react";
import {
    LinkingOptions,
    NavigationContainer,
    NavigatorScreenParams,
} from "@react-navigation/native";
import { navigationRef } from "./navigationRef";
import * as ExpoNotifications from "expo-notifications";
import { AuthManagerContext, AuthStatus } from "../contexts/AuthManager";
import { useTheme } from "../hooks/use-theme";
import * as Sentry from "@sentry/react-native";
import Constants from "expo-constants";
import queryString from "query-string";
import {
    EarningType,
    Cct,
    MissionFee,
    MissionDeduction,
    InvitationTarget,
    Mission,
    LppPlan,
    ContractSignatureStatus,
} from "../__generated__/graphql";
import {
    CctDetailsFieldFullValueFieldsFragment,
    InvitationFieldsFragment,
} from "../__generated__/operations/graphql";
import { GetSalaryDetailsQuery } from "../__generated__/operations/graphql";
import * as Linking from "expo-linking";
import {
    convertUrlToCustomScheme,
    deepValuesMap,
    getNotificationData,
    IjmSelection,
    sleep,
} from "../utils";
import Navigator from "./Navigator";
import { ClientTabParamList } from "./client/Home";
import { WorkerTabParamList } from "./worker/Home";
import { usePushNotification } from "../hooks/push-notification";
import { useTracking } from "../hooks/tracking";
import { Platform } from "react-native";
import { ListItemProps } from "../ui_atoms/ListItem";
import AppLoadingManager from "../contexts/AppLoadingManager";
import {
    ProfileSwitcherManagerContext,
    ProfileIdType,
    findProfileForUser,
    buildFullProfileId,
} from "../contexts/ProfileSwitcherManager";
import ProfileSwitcher from "../components/ProfileSwitcher";
import WebImageChooserPortal from "../components/webimages/WebimageChooserPortal";
import UserAvatarPortal from "../components/avatars/UserAvatarPortal";
import { useSnackbar } from "../hooks/snackbar";
import { useI18n } from "../hooks/i18n-hooks";
import { SignupSigninScreens } from "./SignupSigninStack";
import { ActionType, ContractSigningCallbackStatus } from "../screens/user/ContractSigningScreen";
import { Buildable, XOR } from "ts-essentials";
import { CreateMissionMode } from "../contexts/CreateMissionManager";
import ModalPortal from "../components/ModalPortal";
import PendingInvitationClaimer from "../components/PendingInvitationClaimer";
import { ContractType } from "../types";
import { useSetAlert } from "../hooks/use-set-alert";
import { useLazyQuery } from "@apollo/client/react";
import { FIND_MY_MISSIONS } from "../graphql/home";
import { useSetAtom } from "jotai";
import { currentLang } from "../atoms/i18n";
import { getLangFromUrl } from "../i18n/config";
import { getWorkerProfileIsComplete } from "../hooks/use-get-is-worker-profile-complete";

export type RootStackParamList = {
    // `invited_by`: who invited the user, `invitation_code`: the code of an invitation sent by
    // email (web only, see LargeLayout).
    Welcome?: { isLogin?: string; invited_by?: string; invitation_code?: string };
    EmailVerifiedSuccess: undefined;
    EmailVerifiedError: undefined;
    SignupSigninStack?: { isSignup?: boolean; initialRouteName?: SignupSigninScreens };
    CheckWorkerInfo?: {
        invitation?: InvitationFieldsFragment;
        invitationCode?: string;
    };
    CheckClientInfo?: {
        isNewProfile: boolean;
        invitation?: InvitationFieldsFragment;
        invitationCode?: string;
    };
    CheckPrivateClientInfo?: { isNewProfile: boolean };
    VeriffScreen: undefined;
    PreIdVerification?: { backOnSkip?: boolean };
    // By id once the invitation is the user's (sent to their phone number, or claimed with its
    // code); by code while signing up, for an invitation sent by email.
    Invitation: { invitationId: string } | { invitationCode: string };
    // The link of an invitation sent by email (`app/invite/?client_name=…&invitation_code=…`).
    InvitationLink?: { invitation_code?: string; client_name?: string };
    IdVerificationPending: undefined;
    IdVerificationRetry: undefined;
    VerificationFailed: undefined;
    PostOnboardingPrepare: undefined;
    SignupFinished: undefined;
    MissionEdit: { missionId: string };
    Home: NavigatorScreenParams<ClientTabParamList> | NavigatorScreenParams<WorkerTabParamList>;
    PostOnboardingProfileEditor?: { newProfileId: ProfileIdType };
    CivilStateEdit?: { profileId: ProfileIdType };
    FiscalAttestationEdit?: { profileId: ProfileIdType };
    EmailEdit?: { profileId?: ProfileIdType; businessEmail?: boolean; lockWhenVerified?: boolean };
    HomeAddressEdit?: { profileId: ProfileIdType };
    ChildrenEdit?: { profileId: ProfileIdType };
    BankAccountEdit: undefined;
    BankAccountEditModal: { profileId?: ProfileIdType }; // for post onboarding
    ExperiencesEdit: undefined;
    ExperiencesEditModal: { profileId?: ProfileIdType }; // for post onboarding
    ClientProfileEdit: undefined;
    WorkerProfileEdit: undefined;
    AvsNumberEdit: undefined;
    PhoneNumberEdit: undefined;
    ClientEdit: undefined;
    ClientContactEdit: undefined;
    AccountEdit: undefined;
    PrivateClientEdit: undefined;
    ConfigureLpp: undefined;
    ContractSigningScreen: {
        contractId: string;
        initialState?: ActionType;
        contractType: ContractType;
        status?: ContractSigningCallbackStatus;
    };
    AmendmentCancellation: { amendmentId: string; mode: "cancel" | "reject" };
    CreateMissionStack?: {
        initialMission?: Buildable<Mission>;
        createMissionMode?: CreateMissionMode;
    };
    CreateContractPreSign: { contractType?: ContractType; contractId: string };
    MissionDetails: { missionId: string };
    MissionsMultiSign: { missionIds: string[] };
    CctDetails: {
        cct: Cct;
        cctDetails: CctDetailsFieldFullValueFieldsFragment;
        useLseCct?: boolean;
    };
    MissionAnnexes: { missionId: string };
    MissionTermination: { missionId: string } | undefined;
    MissionTimeSheets: { missionId: string };
    MissionClientFees: { fees: ({ key: string } & ListItemProps)[] };
    MissionInfoDeductions: { deductions: ({ key: string } & ListItemProps)[] };
    SalaryCountingDetails: {
        earningType: EarningType;
        // Whether the vacation indemnity is paid out with the salary (vs. held as a provision /
        // cagnotte). When false on an hourly/daily contract, the worker part takes the "Provisions
        // vacances" out of the gross before the deductions and ends on "Salaire versé à l'employé".
        payVacationIndemnityWithSalary: boolean;
        // Whether the mission's legal employer is LSE. Otherwise (co-employment) the client is not
        // invoiced and pays the period's salary and charges itself.
        useLseCct: boolean;
        // Whether the 13th salary is paid with every salary (12x). False on a monthly 13x contract: the
        // 13th share is held back like a vacation cagnotte and paid on release.
        pay13thSalaryWithSalary: boolean;
        salaryDetails: Pick<
            GetSalaryDetailsQuery["getSalaryDetails"],
            | "workerDeductions"
            | "invoiceSalary"
            | "netSalary"
            | "deferredIndemnity"
            | "lseMargin"
            | "grossSalary"
            | "vacationCompensation"
            | "publicHolidaysCompensation"
            | "thirteenMonthCompensation"
            | "baseSalary"
            | "clientDeductions"
        >;
    };
    PayrollBreakdownList: { missionId: string };
    PayrollBreakdownDetails: { payrollBreakdownId: string };
    PayrollBreakdownEditor: { payrollBreakdownId: string };
    PayrollBreakdownTimeSheetScreen: { payrollBreakdownId: string };
    TimesheetList: undefined;
    TimesheetClientReview: { id: string };
    TimesheetWorkerReview: { id: string };
    TimesheetWorkerInput: { id: string };
    ResendEmailVerification: { clientProfile: boolean };
    Confirmation: undefined;
    PdfViewer: XOR<{ assetId: string }, { assetUrl: string }> & {
        title?: string;
        topMarkdown?: { text: string; color?: string };
        filename?: string;
    };
    // Session (document + non-serialisable callbacks) lives in DocumentManager and is read through
    // context, so this screen carries no params. See components/document/DocumentEditorScreen.tsx.
    DocumentEditor: undefined;
    SendInvitation: {
        target: InvitationTarget;
        invitationId?: string; // for existing invitations
    };
    InviteClientInfo: undefined;
    IntercomScreen: undefined;
    ClientList: undefined;
    InvoiceDetails: { invoiceId: string };
    SalaryDetails: { salaryId: string };
    SalaryPayslips: { salaryId: string };
    PayslipDetails: { payslipId: string };
    FeesDetails: { fees: MissionFee[]; deductions: MissionDeduction[] };
    NotFoundScreen?: { error?: Error; goBack?: boolean; isNotFound?: boolean };
    SkribbleSampleScreen: undefined;
    IJMEditorNavScreen: {
        ijmSelection?: Buildable<IjmSelection> | undefined;
        salaryInfo?:
            | {
                  amount: number;
                  earningType: EarningType;
              }
            | undefined;
        cct?: Cct;
        useLse: boolean;
    };
    ContractPreviewScreen: {
        contractType?: ContractType;
        contractId: string;
    };
    LPPEditorNavScreen: {
        lppPlan?: Buildable<LppPlan> | undefined;
        cct?: Cct;
        useLse: boolean;
        availablePlans: LppPlan[];
    };
    VacationEditorNavScreen: {
        vacationDays?: number;
        payVacationIndemnityWithSalary: boolean;
        isCagnotteMandatory: boolean;
        isVacationTracked: boolean;
        minVacationDays: number;
        contractPercentage: number;
        earningType: EarningType;
    };
    ThirteenthSalaryEditorNavScreen: {
        has13thSalary: boolean;
        pay13thSalaryWithSalary: boolean;
        isMandatory: boolean;
        earningType: EarningType;
    };
    MissionSelectSignerScreen: {
        missionId: string;
        showCurrentProfile?: boolean;
        amendmentId?: string;
    };
    IJMSelectionViewer: { cctLabel?: string; ijmSelection?: IjmSelection };
};

const prefix = Linking.createURL("", { scheme: Constants.expoConfig?.extra?.scheme as string });

const linkingConfig = {
    initialRouteName: "Home",
    screens: {
        Welcome: "app/welcome",
        EmailVerifiedSuccess: "app/email_verified_success",
        EmailVerifiedError: "app/email_verified_error",
        SignupSigninStack: {
            initialRouteName: "PhoneNumber",
            screens: {
                PhoneNumber: "app/welcome/phone_number",
                ConfirmCode: "app/welcome/confirm_code",
                SelectRole: "app/welcome/select_role",
                InvitationCode: "app/welcome/invitation_code",
                CheckWorkerInfo: "app/welcome/check_worker_info",
                CheckClientInfo: "app/welcome/check_client_info",
                CheckPrivateClientInfo: "app/welcome/check_private_client_info",
            },
        },
        CheckWorkerInfo: "app/check_worker_info",
        CheckClientInfo: "app/check_client_info",
        CheckPrivateClientInfo: "app/check_private_client_info",
        InvoicesTab: "app/invoices",
        VeriffScreen: "app/veriff_screen",
        PreIdVerification: "app/pre_id_verification",
        IdVerificationPending: "app/id_verification_pending",
        IdVerificationRetry: "app/id_veritifcation_retry",
        VerificationFailed: "app/verification_failed",
        PostOnboardingPrepare: "app/post_onboarding_prepare",
        SignupFinished: "app/signup_finished",
        PostOnboardingProfileEditor: "app/post_onboarding_profile_editor",
        CivilStateEdit: "app/civil_state_edit",
        FiscalAttestationEdit: "app/fiscal_attestation_edit",
        EmailEdit: "app/email_edit",
        HomeAddressEdit: "app/home_address_edit",
        ChildrenEdit: "app/children_edit",
        BankAccountEdit: "app/bank_account_edit",
        BankAccountEditModal: "app/bank_account_edit_modal",
        ExperiencesEditModal: "app/experiences_edit_modal",
        DiplomaAndTrainingEdit: "app/diploma_and_training_edit",
        ClientProfileEdit: "app/client_profile_edit",
        WorkerProfileEdit: "app/worker_profile_edit",
        PhoneNumberEdit: "app/phone_number_edit",
        ClientEdit: "app/client_edit",
        ClientContactEdit: "app/client_contact_edit",
        AccountEdit: "app/account_edit",
        PrivateClientEdit: "app/private_client_edit",
        ConfigureLpp: "app/configure_lpp",
        MissionDetails: "app/mission_details",
        CctDetails: "app/cct_details",
        MissionAnnexes: "app/mission_annexes",
        MissionTermination: "app/mission_termination",
        AmendmentCancellation: "app/amendment_cancellation",
        MissionTimeSheets: "app/mission_timesheets",
        MissionInfoDeductions: "app/mission_info_deductions",
        MissionLogs: "app/mission_logs",
        SalaryCountingDetails: "app/salary_counting_details",
        PayrollBreakdownList: "app/payroll_breakdown_list",
        PayrollBreakdownTimeSheetScreen: "app/payroll_breakdown_timesheet_screen",
        TimesheetList: "app/timesheet_list",
        ResendEmailVerification: "app/resend_email_verification",
        Confirmation: "app/confirmation",
        PdfViewer: "app/pdf_viewer",
        SendInvitation: "app/send_invitation",
        InviteClientInfo: "app/invite_client_info",
        IntercomScreen: "app/intercom_screen",
        ClientList: "app/client_list",
        FeesDetails: "app/fees_details",
        NotFoundScreen: "app/not_found_screen",
        SkribbleSampleScreen: "app/skribble_sample_screen",
        IJMEditorNavScreen: "app/ijm_editor_nav_screen",
        IJMSelectionViewer: "app/ijm_selection_viewer",
        TimesheetWorkerInput: "app/timesheets/:id",
        TimesheetWorkerReview: "app/timesheets/:id/worker_review",
        TimesheetClientReview: "app/timesheets/:id/client_review",
        SalaryDetails: "app/salaries/:salaryId",
        PayslipDetails: "app/payslips/:payslipId",
        InvoiceDetails: "app/invoices/:invoiceId",
        Home: {
            initialRouteName: "MissionsScreen",
            screens: {
                SalariesScreen: "app/salaries",
                MissionsScreen: {
                    initialRouteName: "MissionList",
                    screens: {
                        MissionList: "app/missions",
                        MissionDetails: "app/missions/:missionId",
                        MissionClientFees: "app/missions/client_fees",
                        MissionInfoDeductions: "app/missions/:missionId/info_deductions",
                        MissionTimeSheets: "app/missions/:missionId/timesheets",
                        MissionLogs: "app/missions/:missionId/logs",
                        PayrollBreakdownList: "app/missions/:missionId/payroll_breakdowns",
                        PayrollBreakdownDetails:
                            "app/missions/payroll_breakdown/:payrollBreakdownId",
                        PayrollBreakdownTimeSheetScreen:
                            "app/missions/payroll_breakdown/:payrollBreakdownId/timesheet",
                    },
                },
                WorkersScreen: {
                    initialRouteName: "WorkerList",
                    screens: {
                        WorkerList: "app/employees",
                        WorkerDetails: "app/employees/detail",
                        WorkerQualifications: "app/employees/detail/qualifications",
                        WorkerMissionList: "app/employees/detail/missions",
                        WorkerTimesheetList: "app/employees/detail/timesheets",
                        MissionDetails: "app/employees/detail/missions/detail",
                        WorkerInvoices: "app/employees/detail/invoices",
                    },
                },
                ActivitiesTab: {
                    initialRouteName: "TodoList",
                    screens: {
                        TodoList: "app/todos",
                        MissionDetails: "app/todos/missions/:missionId",
                    },
                },
                WorkerTimesheetList: "app/timesheets",
                ProfileStack: {
                    initialRouteName: "Profile",
                    screens: {
                        Profile: "app/profile",
                        UserLogs: "app/profile/logs",
                        Invoices: "app/profile/invoices",
                        Colleagues: "app/profile/colleagues",
                    },
                },
            },
        },
        ContractSigningScreen: "app/contract_signing/:contractId",
        ContractPreviewScreen: "app/contract_preview/:contractId",
        MissionSelectSignerScreen: "app/mission_select_signer/:missionId",
        PayrollBreakdownDetails: "app/payroll_breakdowns/details/:payrollBreakdownId",
        PayrollBreakdownEditor: "app/payroll_breakdowns/review/:payrollBreakdownId",
        Invitation: "app/invitations/:invitationId",
        InvitationLink: "app/invite",
    },
};

const routesList = deepValuesMap(linkingConfig, "initialRouteName");

// Query parameters a screen reads from its route params: React Navigation turns the query string of
// a path into the params of the screen it opens. `applyDeeplinkQuery` consumes the others
// (`profile_id`, `lang`), which are dropped.
const ROUTE_QUERY_PARAMS = ["invitation_code", "client_name"];

// `url` (a link resolved to a path of the app) with the route query parameters of `rootUrl` (the
// link as it was opened).
const withRouteQuery = (url: string, rootUrl: string) => {
    const { query } = queryString.parseUrl(rootUrl);
    const routeQuery = queryString.stringify(
        Object.fromEntries(Object.entries(query).filter(([k]) => ROUTE_QUERY_PARAMS.includes(k))),
    );
    return routeQuery ? `${url}?${routeQuery}` : url;
};

const matchRoute = (route: string) =>
    !!routesList.find((v) => {
        const regexPattern = v.replace(/:[^\\/]+/g, "[^\\/]+");
        const regex = new RegExp(`^${regexPattern}$`);
        return regex.test(route);
    });

export default function RootNavigation() {
    const theme = useTheme();
    const { status, user, refreshUser } = use(AuthManagerContext);
    const { profileId: currentProfileId, setProfile } = use(ProfileSwitcherManagerContext);
    const routeNameRef = useRef<string>(undefined);
    const { trackScreenView } = useTracking();
    const { showSnackbar } = useSnackbar();
    const i18n = useI18n();
    const setLang = useSetAtom(currentLang);

    usePushNotification();

    const applyDeeplinkQuery = useCallback(
        async (rootUrl: string) => {
            if (typeof rootUrl !== "string") {
                console.warn("applyDeeplinkQuery - rootUrl is not a string", typeof rootUrl);
                return "";
            }

            const { url, query } = queryString.parseUrl(rootUrl);

            // Deeplinks generated outside the app (e.g. email verification links) carry the
            // language they were built for: apply it before navigating so the target screen
            // renders in that language instead of the device default.
            const urlLang = getLangFromUrl(rootUrl);
            if (urlLang) {
                setLang(urlLang);
            }

            if (Object.keys(query).length > 0) {
                const { profile_id } = query as { profile_id?: string };

                let currentUser = user;

                if (!currentUser) {
                    // In the case of getInitialURL(), `user` is undefined so we have to refetch it
                    currentUser = await refreshUser?.();
                }

                if (currentUser && profile_id && profile_id !== currentProfileId) {
                    const profile = findProfileForUser(profile_id, currentUser);
                    if (profile) {
                        await setProfile(profile);
                    } else {
                        showSnackbar({
                            text: i18n.t("profile_switch.not_found"),
                            mode: "error",
                        });
                    }
                }
            }

            return url;
        },
        [user, currentProfileId, refreshUser, setProfile, showSnackbar, i18n, setLang],
    );

    const linking: LinkingOptions<RootStackParamList> = {
        prefixes: [prefix],
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        config: linkingConfig,
        async getInitialURL() {
            // Check if app was opened from a deep link
            let initialUrl = await Linking.getInitialURL();

            if (initialUrl != null) {
                const openedUrl = initialUrl;
                initialUrl = await applyDeeplinkQuery(initialUrl);

                let finalUrl = initialUrl.replace(/\/+$/, "");

                if (initialUrl.startsWith("https://") || initialUrl.startsWith("http://")) {
                    // if the deep link is a web url, we want to extract the path
                    finalUrl = convertUrlToCustomScheme(finalUrl);
                }

                if (finalUrl.endsWith("/app")) {
                    if (status === AuthStatus.In) {
                        finalUrl = `${prefix}app/missions`;
                    } else {
                        finalUrl = `${prefix}app/welcome`;
                    }
                }
                if (matchRoute(finalUrl.replace(prefix, ""))) {
                    return withRouteQuery(finalUrl, openedUrl);
                } else {
                    Sentry.captureException(
                        new Error(`The following link does not exists in the app: ${finalUrl}`),
                    );
                }
            }

            // Handle URL from expo push notifications
            const response = await ExpoNotifications.getLastNotificationResponseAsync();

            const appUrl = response
                ? (getNotificationData(response.notification.request.content).app_url as string)
                : undefined;

            return appUrl ? await applyDeeplinkQuery(appUrl) : "/";
        },
        // Custom function to subscribe to incoming links
        subscribe(listener) {
            const unsubscribe = ExpoNotifications.addNotificationResponseReceivedListener(
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                async (notificationResponse) => {
                    try {
                        listener(
                            await applyDeeplinkQuery(
                                getNotificationData(
                                    notificationResponse.notification.request.content,
                                ).app_url as string,
                            ),
                        );
                    } catch (error) {
                        // catch the exception to avoid unicorn crash on the app when notification
                        // are badly formatted
                        Sentry.captureException(error, {
                            data: {
                                info: "Error while handling notification response",
                                contentName:
                                    notificationResponse.notification.request.content?.title,
                                contentData:
                                    notificationResponse.notification.request.content?.data,
                            },
                        });
                    }
                },
            );
            const linkingSubscription = Linking.addEventListener("url", ({ url }) => {
                // The trailing slash is removed once the query is (`…/app/invite/?invitation_code=…`).
                void applyDeeplinkQuery(url).then((resolvedUrl) => {
                    let finalUrl = resolvedUrl.replace(/\/+$/, "");
                    if (finalUrl.startsWith("https://") || finalUrl.startsWith("http://")) {
                        // if the deep link is a web url, we want to extract the path
                        finalUrl = convertUrlToCustomScheme(finalUrl);
                    }

                    if (finalUrl.endsWith("/app")) {
                        if (status === AuthStatus.In) {
                            finalUrl = `${prefix}app/missions`;
                        } else {
                            finalUrl = `${prefix}app/welcome`;
                        }
                    }
                    if (matchRoute(finalUrl.replace(prefix, ""))) {
                        listener(withRouteQuery(finalUrl, url));
                    } else {
                        Sentry.captureException(
                            new Error(`The following link does not exists in the app: ${finalUrl}`),
                        );
                    }
                });
            });

            return () => {
                // Clean up the event listeners
                unsubscribe.remove();
                linkingSubscription.remove();
            };
        },
    };
    const { setAlert } = useSetAlert();

    const [findMyMissions] = useLazyQuery(FIND_MY_MISSIONS, { fetchPolicy: "no-cache" });

    const checkWorkerProfilePendingSignatures = useCallback(async () => {
        const workerProfile = user?.workerProfile;

        // The mission screens are not registered until the worker onboarding is complete (see
        // Navigator), so the alert below would lead nowhere: the onboarding comes first.
        if (workerProfile && getWorkerProfileIsComplete(user).isValid) {
            // The backend validates the Lift-Profile header id as a UUID. Fall back to a placeholder
            // UUID when the profile id is missing/invalid (e.g. in dev) so the query does not fail
            // with `"%(value)s" is not a valid UUID.`
            const PLACEHOLDER_PROFILE_UUID = "00000000-0000-0000-0000-000000000000";
            const isValidUuid = (value?: string) =>
                !!value &&
                /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
            const safeWorkerProfile = isValidUuid(workerProfile.id)
                ? workerProfile
                : { ...workerProfile, id: PLACEHOLDER_PROFILE_UUID };
            const myWorkerMissions = await findMyMissions({
                context: {
                    liftProfile: buildFullProfileId(safeWorkerProfile).fullProfileId,
                    queryDeduplication: false,
                },
            });

            const now = Date.now();
            const missionsNeedingSelfSign =
                myWorkerMissions.data?.findMyMissions?.elements?.filter((mission) => {
                    const contract = mission.currentContract;
                    if (!contract?.signatureStatus || !contract.startDate) {
                        return false;
                    }
                    const hasStarted = new Date(contract.startDate).getTime() <= now;
                    return (
                        hasStarted &&
                        [
                            ContractSignatureStatus.NeedSelfSign,
                            ContractSignatureStatus.WaitingSelfSign,
                        ].includes(contract.signatureStatus)
                    );
                }) ?? [];

            if (missionsNeedingSelfSign.length > 0) {
                const missionToSign = missionsNeedingSelfSign[0];
                const switchToWorkerProfile = async () => {
                    await setProfile(workerProfile);
                    await sleep(0); // ensure that the profile switch has been processed and new stack is rendered before navigating
                };
                setAlert({
                    title: i18n.t("alerts.mission_needs_signature.title"),
                    message: i18n.t("alerts.mission_needs_signature.description"),
                    canDismiss: false,
                    buttons: [
                        {
                            // Signing is only offered from the mission details, where the annexes
                            // can be reviewed before signing.
                            text: i18n.t("alerts.mission_needs_signature.view_contract"),
                            onPress: () => {
                                void switchToWorkerProfile().then(() => {
                                    navigationRef.navigate("Home", {
                                        screen: "MissionsScreen",
                                        params: {
                                            screen: "MissionDetails",
                                            params: { missionId: missionToSign.id },
                                        },
                                    });
                                });
                            },
                        },
                    ],
                });
            }
        }
    }, [findMyMissions, i18n, setAlert, setProfile, user]);

    const onNavigationReady = useCallback(() => {
        const currentRoute = navigationRef?.getCurrentRoute();
        routeNameRef.current = currentRoute?.name;
        if (Platform.OS === "web") {
            // If there is a trailing profile_id param in the url, ProfileSwitcherManager.tsx:284 will use it then we remove it here
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            navigationRef?.setParams({ profile_id: undefined });
        }
        void checkWorkerProfilePendingSignatures();
    }, [checkWorkerProfilePendingSignatures]);

    const onNavigationStateChange = useCallback(() => {
        const previousRouteName = routeNameRef.current;
        const currentRouteName = navigationRef?.getCurrentRoute()?.name;
        if (currentRouteName && previousRouteName !== currentRouteName) {
            // Save the current route name for later comparison
            routeNameRef.current = currentRouteName;
            void trackScreenView(currentRouteName);
        }
    }, [trackScreenView]);

    return (
        <NavigationContainer<RootStackParamList>
            theme={theme}
            linking={linking}
            ref={navigationRef}
            onReady={onNavigationReady}
            onStateChange={onNavigationStateChange}
            navigationInChildEnabled
        >
            {/* AppLoadingManager must be in the NavigationContainer */}

            <AppLoadingManager>
                <Navigator />
                <PendingInvitationClaimer />
                <ProfileSwitcher />
                <WebImageChooserPortal />
                <UserAvatarPortal />
                <ModalPortal />
            </AppLoadingManager>
        </NavigationContainer>
    );
}
